import React, { FC, memo } from 'react';
import { useFormikContext } from 'formik';
import { useDrawerSearchFilterNameOrCategory } from '@minecraft.drawer';
import { useTranslation } from '@blocs.i18n';
import {
  FiltersNameEnum,
  SizesCategoryEnum,
  SelectOption,
  FilterDrawerType,
  FormItem,
  Label,
  useFiltersLabel,
} from '@blocs.filter';
import { Delimiter, StyledToggler } from '../../shared/styles';
import { FiltersCategoryNameEnum } from '../../shared/types';
import ClothingSizesGeneral from './ClothingSizesGeneral';
import ClothingSizesCategory from './ClothingSizesCategory';
import ClothingSizesMen from './ClothingSizesMen';
import ClothingSizesWomen from './ClothingSizesWomen';
import ClothingSizesChildren from './ClothingSizesChildren';

type Props = {
  showInImperial: boolean;
  type: FilterDrawerType;
  optionsList: { [key: string]: SelectOption[] };
};

const ClothingSizes: FC<Props> = ({ showInImperial, type, optionsList }) => {
  const { t } = useTranslation();
  const filtersLabelOfAudience = useFiltersLabel(type);
  const {
    sleevesOptions,
    chestOptions,
    necksOptions,
    coatsOptions,
    maleShoeSizeOptions,
    maleTshirtSizesOptions,
    inseamsOptions,
    glovesOptions,
    hatsOptions,
    shoeModifierOptions,
    dressSizeOptions,
    bustsOptions,
    underBustsOptions,
    cupsOptions,
    hipsOptions,
    femalePantsSizeOptions,
    femaleShoeSizeOptions,
    femaleTshirtSizesOptions,
    boysSizesOptions,
    girlsSizesOptions,
    toddlersSizesOptions,
    infantsSizesOptions,
    childrenShoeSizeOptions,
    waistsOptions,
  } = optionsList;

  const { values } = useFormikContext();

  const { isSearching } = useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.clothingSizes);

  const isClothingSizesMenVisible = values[FiltersNameEnum.sizesCategory].value === SizesCategoryEnum.men;
  const isClothingSizesWomenVisible = values[FiltersNameEnum.sizesCategory].value === SizesCategoryEnum.women;
  const isClothingSizesChildrenVisible = values[FiltersNameEnum.sizesCategory].value === SizesCategoryEnum.children;

  return (
    <>
      {isSearching && <Label>{t('common:profile.sizes.clothingSizes')}</Label>}
      <div>
        <FormItem $fullWidth $marginValue={2}>
          <StyledToggler title={t('common:profile.sizes.general')}>
            <ClothingSizesGeneral
              showInImperial={showInImperial}
              type={type}
              optionsList={{
                inseamsOptions,
                glovesOptions,
                hatsOptions,
                waistsOptions,
              }}
            />
          </StyledToggler>
        </FormItem>
        <FormItem $fullWidth $marginValue={2}>
          <ClothingSizesCategory
            type={type}
            name={FiltersNameEnum.sizesCategory}
            label={filtersLabelOfAudience[FiltersNameEnum.sizesCategory]}
          />
        </FormItem>
        {(isClothingSizesMenVisible || isClothingSizesWomenVisible || isClothingSizesChildrenVisible) && (
          <>
            <Delimiter />
            <FormItem $fullWidth>
              {isClothingSizesMenVisible && (
                <ClothingSizesMen
                  showInImperial={showInImperial}
                  type={type}
                  optionsList={{
                    chestOptions,
                    necksOptions,
                    sleevesOptions,
                    coatsOptions,
                    maleTshirtSizesOptions,
                    maleShoeSizeOptions,
                    shoeModifierOptions,
                  }}
                />
              )}
              {isClothingSizesWomenVisible && (
                <ClothingSizesWomen
                  showInImperial={showInImperial}
                  type={type}
                  optionsList={{
                    dressSizeOptions,
                    bustsOptions,
                    underBustsOptions,
                    cupsOptions,
                    hipsOptions,
                    femalePantsSizeOptions,
                    femaleTshirtSizesOptions,
                    femaleShoeSizeOptions,
                    shoeModifierOptions,
                  }}
                />
              )}
              {isClothingSizesChildrenVisible && (
                <ClothingSizesChildren
                  type={type}
                  optionsList={{
                    boysSizesOptions,
                    girlsSizesOptions,
                    toddlersSizesOptions,
                    infantsSizesOptions,
                    childrenShoeSizeOptions,
                    shoeModifierOptions,
                  }}
                />
              )}
            </FormItem>
          </>
        )}
      </div>
    </>
  );
};

export default memo(ClothingSizes);
